/**
 * Incident Service for Outlook Add-in
 * Handles incident data fetching from DoNexus API
 */

import apiClient from '../utils/apiClient';

class IncidentService {
  constructor() {
    this.incidentId = null; // Incident ID will be set dynamically from incident communications
  }

  /**
   * Fetch incident data by ID
   * @param {boolean} includeUninitialized - Whether to include uninitialized incidents
   * @returns {Promise<Object>} Incident data object
   */
  async getIncident(includeUninitialized = true) {
    if (!this.incidentId) {
      throw new Error('Incident ID not set. Please ensure incident communications data is loaded first.');
    }

    try {
      const queryParams = includeUninitialized ? '?includeUninitialized=true' : '';
      const response = await apiClient.get(`/incident/${this.incidentId}${queryParams}`);
      
      if (response.success) {
        return {
          success: true,
          data: response.data,
          message: response.message
        };
      } else {
        throw new Error(response.message || 'Failed to fetch incident data');
      }
    } catch (error) {
      console.error('Error fetching incident data:', error);
      throw error;
    }
  }

  /**
   * Get incident ID
   * @returns {string|null} Current incident ID or null
   */
  getIncidentId() {
    return this.incidentId;
  }

  /**
   * Set incident ID
   * @param {string} id - Incident ID
   */
  setIncidentId(id) {
    this.incidentId = id;
  }

  /**
   * Check if incident ID is available
   * @returns {boolean} True if incident ID is set
   */
  hasIncidentId() {
    return this.incidentId !== null;
  }
}

// Create singleton instance
const incidentService = new IncidentService();

export default incidentService;
